import Papa from 'papaparse'
import { SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Select from 'react-select'
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap'
import exportData from '../../../api/exportData/hooks'
import { DataForExportItem } from '../../../api/exportData/types'
import { MODAL_TYPES } from '../../../common/constants'
import { hideModal } from '../../../store/modal'
import { resetTableCheckbox } from '../../../store/table'
import AppPermissionsSwitcher from '../../AppPermissionsSwitcher/AppPermissionsSwitcher'
import Loader from '../../Common/Loader'
import { findMatchingKeys, flattenObject, getSelectedItems, transformData } from './helpers'
import { DefaultDropDown, ExportDataModalProps, PermissionsProps } from './types'
import { useExportDataFormValues } from './useExportDataFormValues'

const ID_LABEL_KEYS: Record<string, string> = {
    property: 'app.common.propertyId',
    project: 'app.common.projectId',
    contact: 'form.contact.general.id',
    company: 'form.company.general.id',
}

const ExportDataModal = (props: ExportDataModalProps) => {
    const { t } = useTranslation()
    const location = useLocation()
    const dispatch = useDispatch()

    const { isOpen, exportDataModal } = props
    const { appModule, params, tableColumns, selectedRowsIds } = exportDataModal

    const [exportAllData, setExportAllData] = useState(false)
    const [dataForExport, setDataForExport] = useState<SetStateAction<DataForExportItem[]>>()
    const [dataExportDropDown, setDataExportDropDown] = useState<DefaultDropDown>()
    const [formatDropDown, setFormatDropDown] = useState<DefaultDropDown>()
    const [optionDropDown, setOptionDropDown] = useState<DefaultDropDown[]>()
    const [exportLoading, setExportLoading] = useState(false)

    const appModuleApi = appModule === 'company' ? 'contact' : appModule

    const { isPendingStatusOptions, statusOptions } = useExportDataFormValues(appModule)
    const activeStatusOptions = optionDropDown ?? statusOptions

    const criteria: { [x: string]: (string | undefined)[] | string } = {
        [`${appModuleApi}Status`]: activeStatusOptions.map((item) => item.value),
    }

    if (appModule === 'company') {
        criteria.contactType = 'company'
    } else if (appModule === 'contact') {
        criteria.contactType = 'person'
    }

    const allDataParams = { criteria }

    const {
        data: apiExportData,
        isLoading: fetchingData,
        isFetching,
    } = exportData.GetDataForExport.useQuery(
        appModuleApi,
        dataExportDropDown?.value === 'shown' ? params : allDataParams,
        dataExportDropDown?.value === 'all' || dataExportDropDown?.value === 'shown' ? true : false,
        exportAllData
    )

    useEffect(() => {
        if (apiExportData) {
            setDataForExport(apiExportData.items)
        }
    }, [apiExportData])

    const exportToCSV = (data: DataForExportItem[]) => {
        const columnNames = tableColumns
            .map((col) => col.meta?.exportId)
            .filter(Boolean)
            .flat()

        const headerNames = tableColumns.reduce((acc: { [key: string]: string }, column) => {
            const header = column.header
            const exportId = column.meta?.exportId

            if (!header || !exportId) return acc

            const exportIdStr = String(exportId)

            if (exportIdStr === 'internalAppId') {
                acc[exportIdStr] = t(ID_LABEL_KEYS[appModule] ?? 'app.common.id')
                return acc
            }

            if (
                exportIdStr.startsWith('primaryAgent_') ||
                exportIdStr.startsWith('agent_') ||
                exportIdStr.startsWith('contactPerson_') ||
                exportIdStr.startsWith('owner_')
            ) {
                acc[column.meta?.fieldName ?? ''] = header
                return acc
            }

            const wildcardKey = exportIdStr.replace(/\d+/, '*')

            if (!acc[wildcardKey]) {
                acc[wildcardKey] = header
            }

            return acc
        }, {})

        const printValueByExportId: { [exportId: string]: (row: DataForExportItem) => string } = {}

        tableColumns.forEach((column) => {
            if (!column.printValue || !column.meta?.exportId) return

            const exportIds = Array.isArray(column.meta.exportId) ? column.meta.exportId : [column.meta.exportId]

            exportIds.forEach((exportId) => {
                printValueByExportId[exportId] = column.printValue as (row: DataForExportItem) => string
            })
        })

        const mappedData = data.map((originalItem) => {
            const item = flattenObject(originalItem)
            const mapped: { [key: string]: string | undefined } = {}

            columnNames.forEach((key: string | undefined) => {
                if (key === 'actions' || key === '#' || key === 'mainPhoto') {
                    return
                }

                const printValue = key && printValueByExportId[key]
                
                if (printValue) {
                    mapped[key] = originalItem ? printValue(originalItem) : undefined
                    return
                }

                const matchingKeys = findMatchingKeys(item, key as string)
                if (matchingKeys.length > 0) {
                    matchingKeys.forEach((matchKey: string) => {
                        if (matchKey.startsWith('propertyEquipment')) {
                            //This sets elevator column to YES
                            item && item[matchKey as keyof DataForExportItem] === 17 ? (mapped[matchKey] = t('view.common.conclusion.yes')) : null
                        } else if (matchKey.startsWith('propertyWebsite')) {
                            //This sets website column to YES
                            mapped[matchKey] = t('view.common.conclusion.yes')
                        } else {
                            mapped[matchKey] = item && item[matchKey as keyof DataForExportItem]
                        }
                    })
                } else {
                    mapped[key as string] = item && item[key as keyof DataForExportItem]
                }
            })

            return mapped
        })

        const result = mappedData.map((data) => transformData(data, headerNames))

        const papaData =
            result &&
            Papa.unparse(result, {
                delimiter: ';',
            })

        const blob = new Blob(['\uFEFF' + papaData], {
            type: 'text/csv;charset=utf-8;',
        })

        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `DimediaCRM_export_${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    const handleDataExportDropDown = (e: DefaultDropDown) => {
        setDataExportDropDown(e)
        switch (e.value) {
            case 'all':
                setExportAllData(true)
                break
            case 'shown':
                setExportAllData(false)
                break
            case 'selected': {
                const selectedItems = getSelectedItems(selectedRowsIds, location.pathname, params)
                setDataForExport(selectedItems)
                break
            }
        }
    }

    const handleOptionDropDown = (e: DefaultDropDown[]) => {
        setOptionDropDown(e)
    }

    const handleExportData = async () => {
        setExportLoading(true)

        await new Promise<void>((resolve) => {
            setTimeout(() => {
                dataForExport && exportToCSV(dataForExport as DataForExportItem[])
                resolve()
            }, 0)
        })

        setExportLoading(false)
    }

    const handleClose = () => {
        dispatch(hideModal({ modalType: MODAL_TYPES.EXPORT_DATA_MODAL }))
        dispatch(resetTableCheckbox(true))
    }

    return (
        <>
            <Modal size="md" isOpen={isOpen}>
                <div className="p-3">
                    <ModalHeader toggle={handleClose} className="border-bottom pb-3">
                        <span className="h5">{t('app.common.exportData.title')}</span>
                    </ModalHeader>
                    {fetchingData || isFetching || isPendingStatusOptions ? (
                        <div className="m-4">
                            <Loader />
                        </div>
                    ) : (
                        <ModalBody>
                            <div className="py-2">
                                <span className="p">{t('app.common.exportData.format')}</span>
                                <Select
                                    placeholder={null}
                                    options={[{ label: `CSV`, value: 'csv' }]}
                                    value={formatDropDown}
                                    onChange={(e) => setFormatDropDown(e as DefaultDropDown)}
                                />
                            </div>
                            <div className="py-2">
                                <span className="p">{t('view.contact.tab.general')}</span>
                                <Select
                                    onChange={(e) => handleOptionDropDown(e as DefaultDropDown[])}
                                    placeholder={null}
                                    options={statusOptions}
                                    defaultValue={statusOptions}
                                    value={activeStatusOptions}
                                    isMulti={true}
                                    isDisabled={dataExportDropDown?.value === 'selected' || dataExportDropDown?.value === 'shown'}
                                />
                            </div>
                            <div className="py-2">
                                <span className="p">{t('app.common.exportData.data')}</span>
                                <AppPermissionsSwitcher permission="ExportData">
                                    {({ canExportAll }: PermissionsProps) => (
                                        <Select
                                            placeholder={null}
                                            options={
                                                [
                                                    canExportAll && {
                                                        label: t('app.common.exportData.dropDownAllRecords'),
                                                        value: 'all',
                                                        isDisabled: selectedRowsIds.length,
                                                    },
                                                    {
                                                        label: t('app.common.exportData.dropDownDisplayedRecords'),
                                                        value: 'shown',
                                                        isDisabled: selectedRowsIds.length,
                                                    },
                                                    {
                                                        label: t('app.common.exportData.dropDownSelectedRecords'),
                                                        value: 'selected',
                                                        isDisabled: !selectedRowsIds.length,
                                                    },
                                                ].filter(Boolean) as DefaultDropDown[]
                                            }
                                            onChange={(e) => handleDataExportDropDown(e as DefaultDropDown)}
                                            value={dataExportDropDown}
                                        />
                                    )}
                                </AppPermissionsSwitcher>
                            </div>
                            {exportLoading ? (
                                <Loader />
                            ) : (
                                <div className="d-flex justify-content-end pt-4">
                                    <Button color="primary" className="btn-label" onClick={handleClose}>
                                        <i className="mdi mdi-close label-icon align-middle fs-16 me-2"></i>
                                        {t('button.cancel')}
                                    </Button>
                                    <Button
                                        color="add"
                                        className="btn-label ms-2"
                                        disabled={!dataExportDropDown || !formatDropDown || activeStatusOptions.length === 0}
                                        onClick={handleExportData}
                                    >
                                        <i className="mdi mdi-arrow-right align-bottom label-icon align-middle fs-16 me-2"></i>
                                        {t('button.generateExport')}
                                    </Button>
                                </div>
                            )}
                        </ModalBody>
                    )}
                </div>
            </Modal>
        </>
    )
}

export default ExportDataModal
